import Button from "components/form/Button";
import { CanceledAgreementIcon } from "icons/action/CancelAgreement";
import { FailedAgreementIcon } from "icons/action/FailedIcon";
import { SuccessPaymentIcon } from "icons/payments/successPayment";
import { QuoteexpiredIcon } from "icons/warning/Quoteexpired";
import moment from "moment";
import React from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams } from "react-router-dom";
import { formatPaymentListDate, formatDisplayDate } from "utils/DateFormat";
import {
  changeScheduleName,
  decode,
  encode,
  formatCurrency,
} from "utils/HelperFunctions";
import { useTestAttrsFactory, useTid } from "utils/testid";

interface QuoteListProps {
  quoteData: any;
  isLoading: boolean;
  OnSubmit: (value: string) => void;
  acceptQuote: string | null;
  isFutureSub?: boolean;
  setIsPreview?: any;
  setIsPreviewId?: any;
  isAcceptLoading?: boolean;
  isDeclineLoading?: boolean;
  invoiceId?: any;
}

interface QuoteItem {
  priceDetails: {
    frequency: string;
  };
  total_price: string | number;
}
const QuotesList = (props: QuoteListProps) => {
  const {
    quoteData,
    isLoading,
    acceptQuote,
    isFutureSub,
    OnSubmit,
    setIsPreview,
    setIsPreviewId,
    isAcceptLoading = false,
    isDeclineLoading = false,
    invoiceId,
  } = props;
  const { id: actionId } = useParams();
  const slug = decode(actionId || "")?.split("_");
  const navigate = useNavigate();
  const tid = useTid();
  const testAttrs = useTestAttrsFactory();

  const recurringTotal: number = (quoteData?.quoteItems as QuoteItem[])
    ?.filter((item: QuoteItem) => item.priceDetails.frequency === "recurring")
    .reduce((sum: number, item: QuoteItem) => {
      const price: number = parseFloat(item.total_price as string) || 0;
      return sum + price;
    }, 0);

  return (
    <div
      className={`relative w-full grid place-items-center ${acceptQuote ? "h-full" : "lg:pt-[120px]"} px-5 pt-5 lg:pb-10 pb-9`}
      {...testAttrs("quote-action", "Quote action")}
    >
      {acceptQuote ? (
        <>
          {acceptQuote === "decline" ? (
            <div className="w-full flex justify-center items-center md:px-[30px] gap-10 flex-col">
              <CanceledAgreementIcon />
              <div className="w-full flex flex-col gap-4 justify-center items-center">
                <div className="grid place-items-center text-txtPrimary lg:text-2xl text-xl font-medium leading-[31.25px]">
                  Quote declined
                </div>
                <div className="text-base text-txtParagraph leading-[20.83px] text-center lg:px-4">
                  You have declined the quote. If you have any questions or need
                  to revise your requirements, please contact us.
                </div>
              </div>
              <div className="text-blue hover:!text-btnPrimaryHover w-full grid place-items-center">
                <span
                  className="cursor-pointer lg:text-base text-sm"
                  role="button"
                  tabIndex={0}
                  {...testAttrs("back-to-dashboard", "Back to dashboard")}
                  onClick={() => {
                    let encodedString = encode(slug[1], "ci_");
                    navigate(`/${encodedString}/dashboard`);
                  }}
                >
                  {" "}
                  Back to dashboard
                </span>
              </div>
            </div>
          ) : acceptQuote === "accept" ? (
            <div className="w-full flex justify-center items-center lg:px-[30px] gap-10 flex-col">
              <SuccessPaymentIcon />
              <div className="w-full flex flex-col gap-4 justify-center items-center">
                <div className=" grid place-items-center text-txtPrimary lg:text-2xl text-xl font-medium leading-[31.25px]">
                  Quote accepted
                </div>
                <div className="text-base text-txtParagraph leading-[20.83px] text-center lg:px-4">
                  {isFutureSub
                    ? `Thank you for accepting the quote. Your subscription begins on ${formatPaymentListDate(quoteData?.subs_start_date, "", "long")}. An invoice will be generated on this date and shared with you for payment.`
                    : "Thank you for accepting the quote. You can immediately view and pay the generated invoice by clicking the 'View Invoice' button below."}
                </div>
              </div>
              <div className="text-blue hover:!text-btnPrimaryHover w-full grid place-items-center">
                {!isFutureSub && (
                  <Button
                    text="View Invoice"
                    {...testAttrs(
                      "view-accepted-quote-invoice",
                      "View accepted quote invoice"
                    )}
                    onClick={() => {
                      setIsPreview(true);
                      setIsPreviewId(invoiceId);
                    }}
                  />
                )}
                <span
                  className={`cursor-pointer ${isFutureSub ? "" : "pt-6"} lg:text-base text-sm`}
                  role="button"
                  tabIndex={0}
                  {...testAttrs("back-to-dashboard", "Back to dashboard")}
                  onClick={() => {
                    let encodedString = encode(slug[1], "ci_");
                    navigate(`/${encodedString}/dashboard`);
                  }}
                >
                  {" "}
                  Back to dashboard
                </span>
              </div>
            </div>
          ) : acceptQuote === "expired" ? (
            <div className="w-full flex justify-center items-center lg:px-[30px] gap-10 flex-col">
              <QuoteexpiredIcon width="99" height="105" />
              <div className="w-full flex flex-col gap-4 justify-center items-center">
                <div className=" grid place-items-center text-txtPrimary lg:text-2xl text-xl font-medium leading-[31.25px]">
                  Quote expired
                </div>
                <div className="text-base text-txtParagraph leading-[20.83px] text-center lg:px-4">
                  This quote is no longer valid. To get an updated quote or if
                  you have any questions, please reach out to us.
                </div>
              </div>
              <div className="text-blue w-full grid place-items-center">
                <span
                  className="cursor-pointer hover:!text-btnPrimaryHover lg:text-base text-sm"
                  role="button"
                  tabIndex={0}
                  {...testAttrs("back-to-dashboard", "Back to dashboard")}
                  onClick={() => {
                    let encodedString = encode(slug[1], "ci_");
                    navigate(`/${encodedString}/dashboard`);
                  }}
                >
                  {" "}
                  Back to dashboard
                </span>
              </div>
            </div>
          ) : acceptQuote === "cancelled" ? (
            <div className="w-full flex justify-center items-center lg:px-[30px] gap-10 flex-col">
              <QuoteexpiredIcon width="99" height="105" />
              <div className="w-full flex flex-col gap-4 justify-center items-center">
                <div className=" grid place-items-center text-txtPrimary lg:text-2xl text-xl font-medium leading-[31.25px]">
                  Quote canceled
                </div>
                <div className="text-base text-txtParagraph leading-[20.83px] text-center lg:px-4">
                  This quote has been canceled. If this was a mistake or you’d
                  like to submit a new request, feel free to contact us.
                </div>
              </div>
              <div className="text-blue w-full grid place-items-center">
                <span
                  className="cursor-pointer hover:!text-btnPrimaryHover lg:text-base text-sm"
                  role="button"
                  tabIndex={0}
                  {...testAttrs("back-to-dashboard", "Back to dashboard")}
                  onClick={() => {
                    let encodedString = encode(slug[1], "ci_");
                    navigate(`/${encodedString}/dashboard`);
                  }}
                >
                  {" "}
                  Back to dashboard
                </span>
              </div>
            </div>
          ) : (
            <>
              <div className=" w-full lg:pt-[150px] pt-[100px] lg:px-[60px] px-5">
                <div className="w-full flex-col flex items-center justify-center gap-10 text-center">
                  <FailedAgreementIcon />
                  <div>
                    <p className="lg:text-2xl text-xl font-medium text-txtPrimary">
                      Action failed
                    </p>
                    <p className="lg:pt-6 pt-4 lg:text-base text-sm text-txtParagraph lg:leading-[20.83px] leading-[18.23px] break-words">
                      We encountered an issue while processing your request.
                      Please try again later. If the problem persists, contact
                      our support team for assistance.
                    </p>
                  </div>
                  <div className="lg:text-base text-sm text-txtParagraph lg:leading-[20.83px] leading-[18.23px]">
                    <div
                      className="lg:text-base text-sm text-blue hover:!text-btnPrimaryHover cursor-pointer "
                      role="button"
                      tabIndex={0}
                      {...testAttrs("back-to-dashboard", "Back to dashboard")}
                      onClick={() => {
                        let encodedString = encode(slug[1], "ci_");
                        navigate(`/${encodedString}/dashboard`);
                      }}
                    >
                      Back to dashboard
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="flex flex-col lg:max-w-[500px] w-full gap-10">
          <div className="flex justify-between items-center">
            <span className="lg:text-xl font-medium text-txtPrimary leading-[26.04px]">
              Quote{" "}
              {isLoading ? (
                <Skeleton height={23} width={80} />
              ) : (
                `#${quoteData?.quote_number}`
              )}
            </span>
          </div>
          <div>
            <div className="w-full h-[1px] bg-strokePrimary"></div>
            {quoteData?.subs_start_date && quoteData?.sub_end_date && (
              <div className="pt-5 flex w-full justify-between items-center text-sm text-txtSecondary">
                {isLoading ? (
                  <Skeleton width={100} />
                ) : (
                  <span>
                    {formatDisplayDate(quoteData?.subs_start_date, "MMM D")} -{" "}
                    {formatDisplayDate(quoteData?.sub_end_date, "MMM D, YYYY")}
                  </span>
                )}
              </div>
            )}
            <div className="pt-5 w-full flex flex-col gap-5">
              {isLoading && (
                <>
                  {[1, 2, 3, 4, 5].map((item, index) => (
                    <div className="w-full" key={index}>
                      <div className="flex justify-between items-center -mb-1">
                        <Skeleton height={13} width={60} />
                        <Skeleton height={13} width={60} />
                      </div>
                      <div className="flex justify-between items-center">
                        <Skeleton height={13} width={60} />
                        <Skeleton height={13} width={60} />
                      </div>
                    </div>
                  ))}
                </>
              )}
              {quoteData?.quoteItems &&
                quoteData?.quoteItems?.length > 0 &&
                !isLoading &&
                [...(quoteData?.quoteItems || [])]
                  .sort((a: any, b: any) => {
                    const aw =
                      a?.priceDetails?.frequency === "recurring" ? 1 : 0;
                    const bw =
                      b?.priceDetails?.frequency === "recurring" ? 1 : 0;
                    return bw - aw;
                  })
                  .map((item: any, index: number) => (
                    <div
                      className="w-full"
                      key={
                        item?.id ||
                        item?.productDetails?.id ||
                        item?.productDetails?.name ||
                        index
                      }
                      data-testid={tid(
                        `quote-action-item-${item?.id || item?.productDetails?.id || item?.productDetails?.name || index}`
                      )}
                    >
                      <div className="flex justify-between items-center text-txtPrimary text-sm font-medium leading-[18.23px]">
                        <span>{item?.productDetails?.name}</span>
                        <span>
                          {formatCurrency(
                            parseFloat(item?.priceDetails.price) *
                              item?.quantity
                          ) || "$0.00"}{" "}
                          {item?.priceDetails?.schedule &&
                            item?.priceDetails?.schedule !== "" &&
                            `/ ${changeScheduleName(item?.priceDetails)}`}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-txtSecondary text-sm leading-[18.23px]">
                        <span>Qty {item.quantity}</span>
                        <span>
                          {formatCurrency(item?.priceDetails?.price) || "$0.00"}{" "}
                          each
                        </span>
                      </div>
                    </div>
                  ))}
              <div className="w-full h-[1px] bg-strokePrimary mt-[10px]" />
            </div>
            <div className="py-5 w-full flex justify-between items-center">
              <span className="uppercase text-sm leading-[15.62px] font-bold text-txtPrimary">
                {recurringTotal > 0 ? "Upfront total" : "Subtotal"}
              </span>
              <span className="uppercase text-lg leading-[18.23px] font-bold text-txtPrimary">
                {isLoading ? (
                  <Skeleton width={80} height={13} />
                ) : (
                  formatCurrency(quoteData?.total_amount) || "$0.00"
                )}
              </span>
            </div>
            {/* <div className="w-full h-[1px] bg-bgSecondary "></div> */}
            <div className="w-full flex justify-between items-center">
              <span className="uppercase text-sm leading-[18.23px] font-bold text-txtPrimary">
                {recurringTotal > 0 ? "Recurring total" : "Total"}
              </span>
              <span className="uppercase text-lg leading-[23.44px] font-bold text-txtPrimary">
                {isLoading ? (
                  <Skeleton width={85} height={18} />
                ) : (
                  formatCurrency(
                    recurringTotal > 0
                      ? recurringTotal
                      : quoteData?.total_amount
                  ) || "$0.00"
                )}
              </span>
            </div>
          </div>
          <div className="flex justify-between items-center gap-5 w-full">
            <Button
              text="Decline"
              variant="white"
              className="w-full"
              isLoading={isDeclineLoading}
              isDisabled={isAcceptLoading}
              {...testAttrs("decline-quote", "Decline quote")}
              onClick={() => OnSubmit("decline")}
            />
            <Button
              text="Accept"
              className="w-full"
              isLoading={isAcceptLoading}
              isDisabled={isDeclineLoading}
              {...testAttrs("accept-quote", "Accept quote")}
              onClick={() => OnSubmit("accept")}
              variant="variable"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotesList;
